/* eslint-disable no-warning-comments */
import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  Link as DSLink,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../../../tests';
import { CF_NEU_MED } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';
import { type ImpactSummaryTypes } from '../../../types';

import {
  SodRemediateFuctionSelect,
  SodRemediateImpactSummary,
  SodRemediateSubmitComment,
} from './sections';

import './SodRemediateModal.css';

interface SodRemediateModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

/**
 * Sod Remediate Modal Flow
 * Step 1: Function Select
 * Step 2: Impact Summary
 * Step 3: Submit Comment
 *
 * * TODO
 * - Write a description
 * - Write IT's
 * - Add isMobileWidth once it is being used again from AAG redux store
 * - Next Button: Submit form
 * - AC for Apply submit comment
 * - DSLink needs update in DS for use here, also needs to be dynamic string
 */
function SodRemediateModal({
  isOpen,
  setIsOpen,
}: SodRemediateModalProps): ReactElement {
  const [isLoading, setIsLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  // Step 1: Function Select
  const [selectedFunction, setSelectedFunction] =
    useState<ImpactSummaryTypes | null>(null);
  // Step 3: Submit Comment
  const [isCommentCritical, setIsCommentCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const sectionRef = useRef(null);
  const l = useInvertedLocalize();

  const isSelectingFunction = currentStep === 1;
  const isReviewingImpactSummary = currentStep === 2;
  const isSubmittingComment = currentStep === 3;

  const modalClasses = cx(
    'SodRemediateModal',
    isSelectingFunction && 'SodRemediateModal--isSelectingFunction',
    isReviewingImpactSummary && 'SodRemediateModal--isReviewingImpactSummary',
    isSubmittingComment && 'SodRemediateModal--isSubmittingComment'
  );
  const stepsBodyClasses = cx(
    'SodRemediateModal-body',
    `SodRemediateModal-body--step${currentStep}`
  );

  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } else {
      setTimeout(() => {
        setIsLoading(true);
      }, 200);
    }
  }, [isOpen]);

  // Reset All On Modal Close, Timeout so the animation does not start until the modal is fully closed
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCurrentStep(1);
        setIsCommentCritical(false);
        setCommentValue('');
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isCommentCritical) {
      setIsCommentCritical(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentValue]);

  // Move to next step on function select
  useEffect(() => {
    if (selectedFunction) {
      setCurrentStep(2);
    }
  }, [selectedFunction]);

  const handleBack = () => {
    // Back
    if (isSubmittingComment) {
      setCurrentStep(2);

      return;
    }

    // Back
    if (isReviewingImpactSummary) {
      setCurrentStep(1);
      setSelectedFunction(null);

      return;
    }

    // Cancel
    setIsOpen(false);
  };

  const handleNext = () => {
    if (isReviewingImpactSummary) {
      setCurrentStep(3);

      return;
    }

    // TODO: Submit form
    setIsOpen(false);
  };

  // Step 0: Loading Data
  const getModalBodyLoading = () => (
    <Box className='SodRemediateModal-bodyLoading'>
      <Loader
        kind='loop'
        format='main'
        color='brand'
        dataTestId={testIds.SOD_REMEDIATE_MODAL_LOADER}
      />
    </Box>
  );

  // Footer
  const getModalFooter = () => (
    <Box className='SodRemediateModal-footerActionButtons'>
      <Button kind='subtle' size='medium' onClick={handleBack}>
        {isReviewingImpactSummary && !selectedFunction
          ? l('Cancel')
          : l('Back')}
      </Button>
      {selectedFunction && (
        <Box className='SodRemediateModal-rightActionButtons'>
          {isReviewingImpactSummary && (
            <Button kind='outlined' size='medium' onClick={() => {}}>
              {l('Save to Simulation')}
            </Button>
          )}
          <Button kind='filled' size='medium' onClick={handleNext}>
            {isSubmittingComment ? l('Request to Revoke') : l('Next')}
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <ModalIsland
      headerIcon={<Icon kind='SodRemediate' size='large' />}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      title={l('Remediate')}
      subtitle={
        <Box className='SodRemediateModal-subtitleWithLink'>
          <Typography kind='body2' color={CF_NEU_MED}>
            {l('Violation:')}
          </Typography>
          <DSLink
            // TODO: Needs DS enhancement, Button style does not have neutral color
            // kind='button'
            color='neutral'
            size='medium'
            onClick={() => {}}
            text={l(
              'OracleCloud_GL_SOD_Enter Journal & Post Journal (jkookamunga)'
            )}
          />
        </Box>
      }
      contentBody={
        <Box className={stepsBodyClasses}>
          {/* Step 0: Loading - Can overlay all other steps */}
          {isLoading && (
            <Box className='SodRemediateModal-bodyStep0'>
              {getModalBodyLoading()}
            </Box>
          )}
          {/* Step 1: Function Select */}
          <Box
            className='SodRemediateModal-bodyStep1'
            aria-hidden={!isSelectingFunction}>
            <SodRemediateFuctionSelect
              isSelectingFunction={isSelectingFunction}
              setSelectedFunction={setSelectedFunction}
            />
          </Box>
          {/* Step 2: Impact Summary */}
          <Box
            className='SodRemediateModal-bodyStep2'
            aria-hidden={!isReviewingImpactSummary}>
            {selectedFunction && (
              <SodRemediateImpactSummary
                selectedFunction={selectedFunction}
                isReviewingImpactSummary={isReviewingImpactSummary}
              />
            )}
          </Box>
          {/* Step 3: Comment Submit */}
          <Box
            className='SodRemediateModal-bodyStep3'
            aria-hidden={!isSubmittingComment}>
            <SodRemediateSubmitComment
              commentValue={commentValue}
              setCommentValue={setCommentValue}
              isSubmittingComment={isSubmittingComment}
              isCommentCritical={isCommentCritical}
            />
          </Box>
        </Box>
      }
      sectionRef={sectionRef}
      FooterActionBarComp={
        !isSelectingFunction ? (
          <FooterActionBar
            size='large'
            isScrolled={sectionRefIsScrolled}
            className='SodRemediateModal-footerActionBar'>
            {getModalFooter()}
          </FooterActionBar>
        ) : undefined
      }
      className={modalClasses}
      sectionClassName='SodRemediateModal-section'
      bodyClassName='SodRemediateModal-bodyWrapper'
    />
  );
}

export default SodRemediateModal;
